import { useState } from 'react';
import { createModel } from 'hox';
import { TablePaginationConfig } from 'antd/es/table/interface';
import { DEFAULT_FIRST_PAGE_NUM, DEFAULT_PAGE_SIZE } from '@/utils/constants';
import { ILplDetail, ILplSearchFields, lplService } from './lplService';
import { useGlobalModel, transPgToPaginationTotal } from '@/model/globalModel';
import { convertRes2Blob } from '@/utils/exportFile';
import { ISearchPage } from '@/types/AppTypes';
import { getRandomStr } from '@/utils/utils';

export interface IDialog {
	visible: boolean,
	codes: string
}

export const useLplModel = createModel(function () {
	const {
		user: { shopCode },
		setLoadingInfo,
	} = useGlobalModel(({ user }) => [user]);
	/**
	 * 列表
	 */
	//列表数据源
	const [dataSource, setDataSource] = useState<ILplDetail[]>([]);
	//查询条件
	const [searchParam, setSearchParam] = useState<ILplSearchFields>({});
	//分页参数
	const initPgCfg: TablePaginationConfig = {
		pageSize: DEFAULT_PAGE_SIZE,
		current: DEFAULT_FIRST_PAGE_NUM,
	};
	const [pgCfg, setPgCfg] = useState<TablePaginationConfig>(initPgCfg);
	//表格多选id值
	const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const initDialog: IDialog = { visible: false, codes: "" }
	//询价单详情
	const [inquireDialog, setInquireDialog] = useState<IDialog>(initDialog);
	//报价单详情
	const [offerDialog, setOfferDialog] = useState<IDialog>(initDialog);
	//采购合同详情
	const [purchaseDialog, setPurchaseDialog] = useState<IDialog>(initDialog);
	//备注详情
	const [noteDialog, setNoteDialog] = useState<{ visible: boolean, row?: ILplDetail }>({ visible: false });

	const setDialog = (type: string, visible: boolean, codes?: undefined | string) => {
		switch (type) {
			case "inquire":
				setInquireDialog(visible ? { visible: true, codes: codes ?? "" } : initDialog);
				break;
			case "offer":
				setOfferDialog(visible ? { visible: true, codes: codes ?? "" } : initDialog);
				break;
			case "purchase":
				setPurchaseDialog(visible ? { visible: true, codes: codes ?? "" } : initDialog);
				break;
			default:
				console.log(type);
		}
	}
	//列表查询
	const loadData = async (
		page: ISearchPage = initPgCfg,
		searchValue: ILplSearchFields = searchParam
	) => {
		setLoading(true);
		const searchParams = {
			shopCode,
			...searchValue,
			pageSize: page?.pageSize ?? pgCfg.pageSize ?? DEFAULT_PAGE_SIZE,
			pageNum: (page?.current || page?.pageNum) ?? pgCfg.current ?? DEFAULT_FIRST_PAGE_NUM,
		};
		let { retData } = await lplService.page(searchParams);
		const { records, ...pg } = retData;
		setPgCfg(transPgToPaginationTotal(pg));
		setDataSource((records ?? []).map(item => ({ ...item, id: getRandomStr() })));
		setLoading(false);
	};
	//设置搜索value
	const updateSearchParam = (updateParam: ILplSearchFields) => {
		setSearchParam({ ...searchParam, ...updateParam });
	};

	//重置搜索参数
	const resetSearchParam = () => {
		setSearchParam({});
		setSelectedRowKeys([]);
		loadData(initPgCfg, {});
	};

	//导出
	const exportLpl = async (searchValue: ILplSearchFields = searchParam) => {
		setLoadingInfo({ loadingVisible: true, txt: '快速导出中' });
		try {
			const searchParams = {
				shopCode,
				...searchValue,
				pageSize: -1,
				pageNum: 1,
			};

			let result = await lplService.export(searchParams);
			result && setLoadingInfo({ loadingVisible: false, txt: '' });
			convertRes2Blob(result);
		} catch {
			setLoadingInfo({ loadingVisible: false, txt: '' });
		}
	};

	//下载模板
	const downloadTpl = async () => {
		let result = await lplService.downloadTpl();
		convertRes2Blob(result);
	};

	return {
		/***field***/
		searchParam,
		dataSource,
		selectedRowKeys,
		pgCfg,
		loading,
		inquireDialog, offerDialog, purchaseDialog,
		/***method***/
		setDataSource,
		setSelectedRowKeys,
		resetSearchParam,
		updateSearchParam,
		loadData,
		exportLpl,
		downloadTpl,
		setDialog,
		setNoteDialog, noteDialog
	};
});
